import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import { createHeraldChatSettingsActions } from "./herald-chat-settings.actions";

export interface HeraldChatSettingsState {
  /** When true, the chat uses the advanced/deep analysis model. */
  isDeepAnalysisEnabled: boolean;
  /** Mirrors the chat streaming status so sibling components can react without prop-drilling. */
  isStreaming: boolean;
  /** True while the Herald pane shows the landing empty state (no user/assistant messages present). */
  isHeraldChatEmpty: boolean;
  /**
   * Route or transport-captured conversation id for chrome that needs the title mid-stream
   * (URL stays `/chat/new` until the first stream finishes).
   */
  activeConversationId: string | null;
}

export interface HeraldChatSettingsActions {
  toggleDeepAnalysis: () => void;
  setIsStreaming: (streaming: boolean) => void;
  setIsHeraldChatEmpty: (empty: boolean) => void;
  setActiveConversationId: (conversationId: string | null) => void;
}

export type HeraldChatSettingsSlice = HeraldChatSettingsState &
  HeraldChatSettingsActions;

export const heraldChatSettingsInitialState: HeraldChatSettingsState = {
  isDeepAnalysisEnabled: false,
  isStreaming: false,
  isHeraldChatEmpty: false,
  activeConversationId: null,
};

const createHeraldChatSettingsSlice: StateCreator<
  HeraldChatSettingsSlice,
  [["zustand/devtools", never]],
  [],
  HeraldChatSettingsSlice
> = (set, get, api) => ({
  ...heraldChatSettingsInitialState,
  ...createHeraldChatSettingsActions(set, get, api),
});

const useHeraldChatSettingsStore = create<HeraldChatSettingsSlice>()(
  devtools(createHeraldChatSettingsSlice, {
    name: "herald-chat-settings",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useHeraldChatSettingsStore;
